import type { NoteDiagnosis } from '@ehr/shared';

/**
 * ehr#682 (PROGRESS_NOTE_10-20-V141 gap #9): the note's diagnosis lives in Session details > Type,
 * place and billing, with V141's "Change" window (openDx). The separate "Diagnosis codes (ICD-10)"
 * card is gone from the note. A New Client note keeps its own Diagnosis section, which its layout
 * places after Mental status (V141 adds one to Intake the same way).
 */
export const dxOwnSection = (kind: string) => kind === 'New Client';

/** V141 isIcd10: a letter, a digit, a digit or letter, then an optional dot and 1 to 4 more. */
export const isIcd10 = (c: string) =>
  /^[A-Z][0-9][0-9A-Z](\.[0-9A-Z]{1,4})?$/.test(c.trim().toUpperCase());

/** One row in the Change window (ehr#741: lib/dxPicker builds them). */
export interface DxRow {
  code: string;
  label: string;
  source?: 'tp' | 'search';
}

/**
 * V141 dxSave: the ticked codes with the Primary one first, the rest in list order. A Primary that
 * isn't ticked falls back to the first ticked code. Null when nothing is ticked.
 */
export function savedDiagnoses(
  rows: readonly DxRow[],
  ticked: ReadonlySet<string>,
  primary: string | null,
): NoteDiagnosis[] | null {
  const inc = rows.filter((r) => ticked.has(r.code));
  if (!inc.length) return null;
  const first = inc.find((r) => r.code === primary) ?? inc[0]!;
  return [first, ...inc.filter((r) => r !== first)].map((r) => ({
    code: r.code,
    label: r.label,
    ...(r.source ? { source: r.source } : {}),
  }));
}

/** V141 dxAddBtn's checks: the error to show, or null when the code can be added. */
export function addError(code: string, label: string, rows: readonly DxRow[]): string | null {
  const c = code.trim().toUpperCase();
  if (!isIcd10(c)) return 'Use an ICD-10 code, like F41.0.';
  if (!label.trim()) return 'Add a description.';
  if (rows.some((r) => r.code === c)) return `${c} is already on the list.`;
  return null;
}
